import { useAppStore } from '@/store/app'
import type { OrgRole } from '@/types'
import { canAccessPath, canPerformAction, displayRoles, normalizeRoles, roleLabel, rolesAllow } from '@/utils/permissions'

const RANK: Record<OrgRole | string, number> = {
  admin:  3,
  owner:  3,
  editor: 2,
  viewer: 1,
}

export function useRole() {
  const { user } = useAppStore()
  const roleName = normalizeRoles(user?.roleName)
  const isSuperAdmin = roleName.includes('iam_superadmin')
  const isOrgAdmin = isSuperAdmin || roleName.includes('iam_org_admin')

  // Prefer the real IAM roles once they've loaded; fall back to the legacy
  // Keycloak-realm-role flag (user.role) only when roleName hasn't arrived yet,
  // so permission-gated UI doesn't flash/incorrectly downgrade during load.
  const role = roleName.length > 0 ? (isOrgAdmin ? 'admin' : 'viewer') : (user?.role ?? 'viewer')
  const rank = RANK[role] ?? 1

  // Highest-priority real role first, for a single meaningful badge label
  // instead of the generic legacy admin/viewer flag or the raw role slug.
  const visibleRoleName = displayRoles(roleName)
  const label = visibleRoleName.length > 0 ? roleLabel(visibleRoleName[0]) : 'Member'

  return {
    role,
    label,
    roleName,
    isSuperAdmin,
    isOrgAdmin,
    isViewer: rank >= 1,
    isEditor: rank >= 2,
    isAdmin:  rank >= 3 || isOrgAdmin,
    can: (required: OrgRole) => rank >= (RANK[required] ?? 1),
    hasAnyRole: (allowedRoles: string[]) => rolesAllow(roleName, allowedRoles, user?.role),
    canAccessPath: (path: string) => canAccessPath(path, roleName, user?.role),
    canAction: (action: string) => canPerformAction(action, roleName, user?.role),
    roleLabel,
  }
}
